/**
 * Service Availability Manager
 * 
 * Intercepts fatal backend errors (e.g., Firestore quota exceeded)
 * and gracefully redirects to the Maintenance page.
 */

class ServiceAvailability {
  constructor() {
    this.isMaintenanceMode = false;
    this.listeners = new Set();
  }

  /**
   * Subscribe to maintenance mode changes
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Determine if an error is a fatal backend failure
   * requiring the system to enter maintenance mode.
   */
  isFatalError(error) {
    if (!error) return false;

    // Check Firebase/Firestore error codes
    const code = error.code || '';

    const fatalCodes = [
      'resource-exhausted', // Daily quota exceeded
      'unavailable',        // Service down
    ];

    if (fatalCodes.includes(code)) {
      return true;
    }

    return false;
  }

  /**
   * Handle an error object. If it's fatal, trigger maintenance mode.
   */
  handleError(error) {
    if (this.isFatalError(error)) {
      this.triggerMaintenance();
    }
  }

  /**
   * Trigger maintenance mode programmatically
   */
  triggerMaintenance() {
    if (!this.isMaintenanceMode) {
      this.isMaintenanceMode = true;
      this.notifyListeners();
    }
  }

  /**
   * Clear maintenance mode
   */
  clearMaintenance() {
    if (this.isMaintenanceMode) {
      this.isMaintenanceMode = false;
      this.notifyListeners();
    }
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener(this.isMaintenanceMode));
  }

  /**
   * Health Check: attempt a small read to see if quota has reset.
   * If successful, it automatically clears maintenance mode.
   */
  async checkHealth() {
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { examDb } = await import('../../config/firebase');
      
      // Perform a minimal 1-document read on a dedicated health node
      const healthRef = doc(examDb, 'system', 'health');
      await getDoc(healthRef);
      
      // Success means the database is reachable and quota is fine
      this.clearMaintenance();
      return true;
    } catch (error) {
      console.warn('Health check failed', error.code, error);
      return false;
    }
  }
}

export const serviceAvailability = new ServiceAvailability();
export default serviceAvailability;
